import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { bookingApi, getAuthSession, physioApi } from '../services/api';
import SlotAvailabilityPicker from '../components/SlotAvailabilityPicker';

function AppointmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedPhysioId = searchParams.get('physioId') || searchParams.get('therapistId') || '';

  const urlDate = searchParams.get('date') || '';
  const urlTime = searchParams.get('time') || '';
  const urlMode = searchParams.get('mode') || 'online';

  const [formData, setFormData] = useState({
    physiotherapistProfileId: selectedPhysioId,
    mode: urlMode,
    offlineAddress: '',
    preferredDate: urlDate,
    preferredTime: urlTime,
    message: ''
  });
  const [physios, setPhysios] = useState([]);
  const [practitionerSlots, setPractitionerSlots] = useState([]);
  const [practitionerWeeklySchedule, setPractitionerWeeklySchedule] = useState(null);
  const [practitionerDuration, setPractitionerDuration] = useState(50);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const authSession = getAuthSession();

  useEffect(() => {
    // Check if user is logged in as a client
    if (!authSession?.token) {
      // User is not logged in, redirect to register page
      navigate('/login?mode=register');
      return;
    }
    
    if (authSession?.user?.role !== 'client') {
      // User is not a client, redirect appropriately
      navigate('/login');
      return;
    }
  }, [authSession, navigate]);

  useEffect(() => {
    setTimeout(() => {
      if (window.WOW) {
        try {
          new window.WOW().init();
        } catch (e) {
          console.log('WOW.js not available');
        }
      }
    }, 500);
  }, []);

  useEffect(() => {
    const loadPhysios = async () => {
      setIsLoading(true);
      try {
        const response = await physioApi.search();
        const rawList = response.profiles || response.therapists || response.users || [];
        const now = new Date();
        const list = rawList.filter((p) => {
          if (p.isSubscriptionEnded === true) return false;
          if (p.membershipStatus === 'expired' || p.membershipStatus === 'cancelled') return false;
          if (p.membershipExpiresAt && new Date(p.membershipExpiresAt) < now) return false;
          if (p.membership === 'pro' && (p.registrationPaymentStatus === 'pending' || p.registrationPaymentStatus === 'failed')) return false;
          return true;
        });
        setPhysios(list);
        if (selectedPhysioId && list.some(p => (p._id || p.id) === selectedPhysioId)) {
          setFormData(prev => ({ ...prev, physiotherapistProfileId: selectedPhysioId }));
          fetchSlotsForTherapist(selectedPhysioId, list);
        } else if (selectedPhysioId && !list.some(p => (p._id || p.id) === selectedPhysioId)) {
          setErrorMessage('Selected practitioner is currently unavailable due to expired membership.');
        }
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load practitioners right now.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPhysios();
  }, [selectedPhysioId]);

  const fetchSlotsForTherapist = async (therapistId, currentPhysiosList = physios) => {
    if (!therapistId) {
      setPractitionerSlots([]);
      setPractitionerWeeklySchedule(null);
      return;
    }
    const match = currentPhysiosList.find(p => (p._id || p.id) === therapistId);
    if (match) {
      setPractitionerDuration(match.consultationDuration || 50);
      setPractitionerWeeklySchedule(match.weeklySchedule || null);
      if (match.availableSlots && match.availableSlots.length > 0) {
        setPractitionerSlots(match.availableSlots.filter(s => !s.isBooked));
        return;
      }
    }

    try {
      setLoadingSlots(true);
      const res = await physioApi.getPublicSlots(therapistId);
      if (res?.availableSlots) {
        setPractitionerSlots(res.availableSlots.filter(s => !s.isBooked));
      } else {
        setPractitionerSlots([]);
      }
      if (res?.therapist?.weeklySchedule || res?.weeklySchedule) {
        setPractitionerWeeklySchedule(res?.therapist?.weeklySchedule || res?.weeklySchedule);
      }
      if (res?.therapist?.consultationDuration || res?.consultationDuration) {
        setPractitionerDuration(res?.therapist?.consultationDuration || res?.consultationDuration);
      }
    } catch (e) {
      console.warn('Could not fetch custom slots:', e);
      setPractitionerSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'physiotherapistProfileId') {
      fetchSlotsForTherapist(value);
    }
  };

  const handleSelectSlot = (slot) => {
    setFormData(prev => ({
      ...prev,
      preferredDate: slot.date,
      preferredTime: slot.startTime,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.physiotherapistProfileId) {
      setErrorMessage('Please select a practitioner.');
      setStatusMessage('');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const scheduledAt = new Date(`${formData.preferredDate}T${formData.preferredTime}:00`).toISOString();

      await bookingApi.create({
        physiotherapistProfileId: formData.physiotherapistProfileId,
        mode: formData.mode,
        scheduledAt,
        offlineAddress: formData.mode === 'offline' ? formData.offlineAddress : '',
        notes: formData.message,
      });

      setStatusMessage('Appointment booked successfully. We will contact you soon.');
      setFormData({
        physiotherapistProfileId: '',
        mode: 'online',
        offlineAddress: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
      });
    } catch (error) {
      setErrorMessage(error.message || 'Unable to complete booking.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Check if redirecting */}
      {!authSession?.token && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>
              🔐 Registration Required
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '20px' }}>
              Please create an account or log in to book an appointment.
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>Redirecting to registration page...</p>
          </div>
        </div>
      )}
      
      {/* Page Header Start */}
      <section className="page-header">
        <div className="page-header__bg" style={{ backgroundImage: 'url(/assets/images/backgrounds/slider-2-2.jpg)' }}></div>
        <div className="container">
          <div className="page-header__inner">
            <h2 className="page-header__title">Book an Appointment</h2>
            <ul className="thm-breadcrumb">
              <li><a href="/">Home</a></li>
              <li><span>/</span></li>
              <li><span>Appointment</span></li>
            </ul>
          </div>
        </div>
      </section>
      {/* Page Header End */}

      {/* Appointment Section Start */}
      <section className="appointment-section" style={{
        padding: '100px 0',
        background: 'linear-gradient(135deg, #f8faff 0%, #eff6ff 100%)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Shapes */}
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '400px', height: '400px', background: 'rgba(124, 58, 237, 0.05)', borderRadius: '50%', filter: 'blur(80px)' }}></div>
        <div style={{ position: 'absolute', bottom: '5%', left: '-5%', width: '300px', height: '300px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '50%', filter: 'blur(60px)' }}></div>

        <div className="container">
          <div className="row align-items-center">
            {/* Left Content Column */}
            <div className="col-xl-5 col-lg-5 wow fadeInLeft" data-wow-duration="1500ms">
              <div style={{ paddingRight: '40px' }}>
                <span style={{ 
                  color: '#7c3aed', 
                  fontWeight: 800, 
                  fontSize: '0.9rem', 
                  textTransform: 'uppercase', 
                  letterSpacing: '2px',
                  display: 'inline-block',
                  marginBottom: '15px',
                  background: 'rgba(124, 58, 237, 0.1)',
                  padding: '6px 15px',
                  borderRadius: '30px'
                }}>Schedule Your Session</span>
                
                <h2 style={{ fontSize: '3.5rem', fontWeight: 900, color: '#1f2937', marginBottom: '25px', lineHeight: '1.1' }}>
                  Your Mental <span style={{ color: '#7c3aed' }}>Health</span> Matters Most.
                </h2>
                <p style={{ fontSize: '1.2rem', color: '#4b5563', marginBottom: '45px', lineHeight: '1.8' }}>
                  Take the first step towards a better you. Our platform connects you with world-class practitioners ready to support your journey.
                </p>

                {/* Steps Visual */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '35px', marginBottom: '40px' }}>
                  <div style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, width: '56px', height: '56px', borderRadius: '18px', background: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.4rem', boxShadow: '0 10px 25px rgba(0,0,0,0.06)', color: '#7c3aed', border: '1px solid #f3f4f6' }}>✨</div>
                    <div>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1f2937', marginBottom: '6px' }}>Verified Specialists</h4>
                      <p style={{ margin: 0, color: '#6b7280', lineHeight: '1.5' }}>Every practitioner on our platform goes through a rigorous verification process.</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, width: '56px', height: '56px', borderRadius: '18px', background: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.4rem', boxShadow: '0 10px 25px rgba(0,0,0,0.06)', color: '#3b82f6', border: '1px solid #f3f4f6' }}>📅</div>
                    <div>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1f2937', marginBottom: '6px' }}>Flexible Scheduling</h4>
                      <p style={{ margin: 0, color: '#6b7280', lineHeight: '1.5' }}>Book sessions that fit your lifestyle—morning, evening, or weekends.</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, width: '56px', height: '56px', borderRadius: '18px', background: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.4rem', boxShadow: '0 10px 25px rgba(0,0,0,0.06)', color: '#10b981', border: '1px solid #f3f4f6' }}>🔒</div>
                    <div>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1f2937', marginBottom: '6px' }}>Private & Secure</h4>
                      <p style={{ margin: 0, color: '#6b7280', lineHeight: '1.5' }}>All conversations and data are encrypted and strictly confidential.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Booking Form Column */}
            <div className="col-xl-7 col-lg-7 wow fadeInRight" data-wow-delay="200ms" data-wow-duration="1500ms">
              <div style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(30px)',
                padding: '60px',
                borderRadius: '40px',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 40px 100px -20px rgba(0, 0, 0, 0.08)'
              }}>
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#1f2937', marginBottom: '12px' }}>Book Your Session</h3>
                  <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>It only takes 60 seconds to request an appointment.</p>
                </div>

                <form className="appointment-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                  <div className="row">
                    <div className="col-md-7">
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Practitioner</label>
                        <select
                          name="physiotherapistProfileId"
                          value={formData.physiotherapistProfileId}
                          onChange={handleChange}
                          className="form-control"
                          required
                          style={{ borderRadius: '18px', height: '60px', border: '2px solid #f3f4f6', background: '#fff', fontSize: '1rem', padding: '0 20px', transition: 'border-color 0.3s' }}
                        >
                          <option value="">Select a Specialist</option>
                          {physios.map((profile) => (
                            <option key={profile._id} value={profile._id}>
                              {profile.userId?.name || profile.name || 'Practitioner'} - {profile.specialization} (₹{profile.consultationFee || 999} for {profile.consultationDuration || 50} min)
                            </option>
                          ))}
                        </select>
                        {(() => {
                          const selectedProfile = physios.find(p => (p._id || p.id) === formData.physiotherapistProfileId);
                          if (!selectedProfile) return null;
                          return (
                            <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>💰 Session Rate:</span>
                              <strong>₹{selectedProfile.consultationFee || 999} for {selectedProfile.consultationDuration || 50} min session</strong>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Session Type</label>
                        <select
                          name="mode"
                          value={formData.mode}
                          onChange={handleChange}
                          className="form-control"
                          required
                          style={{ borderRadius: '18px', height: '60px', border: '2px solid #f3f4f6', background: '#fff', fontSize: '1rem', padding: '0 20px' }}
                        >
                          <option value="online">🌐 Online Video</option>
                          <option value="offline">🏠 Offline/Visit</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Practitioner's Available Slots Section */}
                  {loadingSlots && (
                    <div style={{ padding: '16px 20px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '0.9rem', textAlign: 'center' }}>
                      ⏳ Loading specialist's active time slots...
                    </div>
                  )}

                  {!loadingSlots && (practitionerSlots.length > 0 || (practitionerWeeklySchedule && Object.keys(practitionerWeeklySchedule).length > 0)) && (
                    <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <SlotAvailabilityPicker
                        slots={practitionerSlots}
                        weeklySchedule={practitionerWeeklySchedule}
                        consultationDuration={practitionerDuration}
                        selectedSlot={{ date: formData.preferredDate, startTime: formData.preferredTime }}
                        onSelectSlot={handleSelectSlot}
                        title="Check available slots"
                      />
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Date</label>
                        <input
                          type="date"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleChange}
                          className="form-control"
                          required
                          style={{ borderRadius: '18px', height: '60px', border: '2px solid #f3f4f6', background: '#fff', fontSize: '1rem', padding: '0 20px' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Preferred Time</label>
                        <input
                          type="time"
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleChange}
                          className="form-control"
                          required
                          style={{ borderRadius: '18px', height: '60px', border: '2px solid #f3f4f6', background: '#fff', fontSize: '1rem', padding: '0 20px' }}
                        />
                      </div>
                    </div>
                  </div>

                  {formData.mode === 'offline' && (
                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Location Address</label>
                      <input
                        type="text"
                        name="offlineAddress"
                        placeholder="Enter full address for the visit..."
                        value={formData.offlineAddress}
                        onChange={handleChange}
                        className="form-control"
                        required
                        style={{ borderRadius: '18px', height: '60px', border: '2px solid #f3f4f6', background: '#fff', fontSize: '1rem', padding: '0 20px' }}
                      />
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: '10px', display: 'block' }}>Message for Practitioner (Optional)</label>
                    <textarea
                      name="message"
                      placeholder="Share any specific concerns or details you'd like to discuss..."
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-control"
                      style={{ borderRadius: '18px', border: '2px solid #f3f4f6', background: '#fff', padding: '20px', fontSize: '1rem', lineHeight: '1.6' }}
                    ></textarea>
                  </div>

                  <div style={{ marginTop: '15px' }}>
                    {isLoading && <p style={{ color: '#7c3aed', textAlign: 'center', fontWeight: 600 }}>Syncing practitioners...</p>}
                    {errorMessage && <p style={{ padding: '15px', background: '#fff1f2', color: '#e11d48', borderRadius: '15px', fontSize: '1rem', fontWeight: 600, border: '1px solid #fecdd3', marginBottom: '20px' }}>⚠️ {errorMessage}</p>}
                    {statusMessage && <p style={{ padding: '15px', background: '#f0fdf4', color: '#16a34a', borderRadius: '15px', fontSize: '1rem', fontWeight: 600, border: '1px solid #bbf7d0', marginBottom: '20px' }}>✨ {statusMessage}</p>}
                    
                    <button type="submit" disabled={isSubmitting} className="thm-btn" style={{
                      width: '100%',
                      padding: '20px 0',
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      border: 'none',
                      color: '#fff',
                      boxShadow: '0 20px 40px -10px rgba(124, 58, 237, 0.4)',
                      cursor: isSubmitting ? 'wait' : 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                      {isSubmitting ? 'Confirming...' : 'Request Appointment'}
                    </button>
                    <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '0.9rem' }}>
                      <span style={{ fontSize: '1.1rem' }}>🛡️</span> Secure & SSL Encrypted Booking
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Appointment Section End */}
    </div>
  );
}

export default AppointmentPage;
